import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Grid, Chip, CircularProgress } from '@mui/material';
import { displayService } from '../services/api';

const CABINS_PER_PAGE = 6;          // 3 per row × 2 rows
const PAGE_INTERVAL_MS = 15000;
const REFRESH_INTERVAL_MS = 30000;

// ── Cabin Card ────────────────────────────────────────────────────────────────
function CabinCard({ cabin }) {
  const upcoming  = cabin.upcomingPatients ?? [];
  const allDone   = cabin.totalPatients > 0 && cabin.completedCount === cabin.totalPatients;

  return (
    <Box
      sx={{
        background: 'linear-gradient(145deg, #1a2a3a 0%, #0d1b2a 100%)',
        border: '2px solid #2a4a6a',
        borderRadius: 3,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        height: '100%',
      }}
    >
      {/* Cabin header */}
      <Box
        sx={{
          background: 'linear-gradient(90deg, #1565c0, #0288d1)',
          px: 2,
          py: 1.25,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          fontWeight={800}
          color="white"
          sx={{ fontSize: '1.5rem', letterSpacing: 1, textTransform: 'uppercase' }}
        >
          {cabin.cabinName}
        </Typography>
        <Chip
          label={`${cabin.waitingCount} Waiting`}
          size="small"
          sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, fontSize: '0.85rem' }}
        />
      </Box>

      {/* Doctor info */}
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #2a4a6a' }}>
        <Typography sx={{ fontSize: '1.1rem' }} color="#90caf9" fontWeight={700} noWrap>
          {cabin.doctorName}
        </Typography>
        {cabin.doctorSpecialization && (
          <Typography sx={{ fontSize: '0.9rem' }} color="#546e7a">
            {cabin.doctorSpecialization}
          </Typography>
        )}
      </Box>

      {/* Current patient / Queue complete */}
      {allDone ? (
        <Box sx={{ px: 2, py: 3, background: 'rgba(0,172,193,0.10)', borderBottom: '1px solid #2a4a6a', textAlign: 'center' }}>
          <Typography color="#00acc1" fontWeight={700} sx={{ fontSize: '1.5rem', letterSpacing: 1 }}>
            ✓ Queue Complete
          </Typography>
          <Typography color="#546e7a" sx={{ fontSize: '1rem' }}>All {cabin.totalPatients} patients seen</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: 'rgba(76, 175, 80, 0.10)',
            borderBottom: '1px solid #2a4a6a',
          }}
        >
          <Typography
            color="#66bb6a"
            fontWeight={700}
            sx={{ fontSize: '0.7rem', letterSpacing: 3, textTransform: 'uppercase', display: 'block', mb: 1 }}
          >
            Now Consulting
          </Typography>
          {cabin.currentPatient ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Large token number */}
              <Box
                sx={{
                  background: '#4caf50',
                  color: 'white',
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  fontWeight: 900,
                  fontSize: '3.5rem',
                  lineHeight: 1,
                  minWidth: 80,
                  textAlign: 'center',
                  flexShrink: 0,
                  boxShadow: '0 4px 12px rgba(76,175,80,0.4)',
                }}
              >
                {cabin.currentPatient.queueNo}
              </Box>
              {/* Large patient name */}
              <Typography
                fontWeight={800}
                color="white"
                sx={{ fontSize: '2.4rem', lineHeight: 1.15, wordBreak: 'break-word' }}
              >
                {cabin.currentPatient.patientName}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '1rem' }} color="#546e7a">No appointments scheduled</Typography>
          )}
        </Box>
      )}

      {/* Upcoming queue — up to 10 patients */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Typography
          color="#78909c"
          fontWeight={700}
          sx={{ fontSize: '0.7rem', letterSpacing: 3, textTransform: 'uppercase', px: 2, pt: 1, pb: 0.5, display: 'block' }}
        >
          Queue
        </Typography>

        {upcoming.length === 0 ? (
          <Typography color="#455a64" sx={{ fontSize: '1rem', px: 2, pb: 1 }}>
            No more patients waiting
          </Typography>
        ) : (
          <Box sx={{ flex: 1 }}>
            {upcoming.map((p, i) => (
              <Box
                key={p.queueNo}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: i === 0 ? 1 : 0.7,
                  background: i === 0
                    ? 'rgba(2, 136, 209, 0.18)'
                    : i % 2 === 0
                      ? 'rgba(255,255,255,0.03)'
                      : 'transparent',
                  borderLeft: i === 0 ? '4px solid #0288d1' : '4px solid transparent',
                }}
              >
                {/* Queue number badge */}
                <Box
                  sx={{
                    background: i === 0 ? '#0288d1' : '#37474f',
                    color: i === 0 ? 'white' : '#90caf9',
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.25,
                    fontWeight: 800,
                    fontSize: '1.6rem',
                    lineHeight: 1,
                    minWidth: 52,
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                >
                  {p.queueNo}
                </Box>
                {/* Patient name */}
                <Typography
                  noWrap
                  sx={{
                    flex: 1,
                    fontSize: '1.35rem',
                    fontWeight: i === 0 ? 700 : 500,
                    color: i === 0 ? '#e3f2fd' : '#b0bec5',
                  }}
                >
                  {p.patientName}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Footer stats */}
      <Box
        sx={{
          px: 2,
          py: 0.75,
          background: 'rgba(0,0,0,0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid #1a2a3a',
        }}
      >
        <Typography variant="caption" color="#546e7a">Total: {cabin.totalPatients}</Typography>
        <Typography variant="caption" color="#546e7a">Completed: {cabin.completedCount}</Typography>
      </Box>
    </Box>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function PageProgressBar({ duration }) {
  return (
    <Box sx={{ height: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          background: 'linear-gradient(90deg, #0288d1, #4caf50)',
          transformOrigin: 'left',
          animation: `pageProgress ${duration}ms linear forwards`,
          '@keyframes pageProgress': {
            from: { transform: 'scaleX(0)' },
            to:   { transform: 'scaleX(1)' },
          },
        }}
      />
    </Box>
  );
}

// ── Page dot indicators ───────────────────────────────────────────────────────
function PageDots({ total, current }) {
  if (total <= 1) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
      {Array.from({ length: total }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: i === current ? 24 : 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: i === current ? '#0288d1' : 'rgba(255,255,255,0.2)',
            transition: 'all 0.4s ease',
          }}
        />
      ))}
    </Box>
  );
}

// ── Live Clock ────────────────────────────────────────────────────────────────
function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return time.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ── Main Display Screen ───────────────────────────────────────────────────────
export default function DisplayScreen() {
  const [cabins, setCabins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Chunk cabins into pages of CABINS_PER_PAGE (6 = 3 per row × 2 rows)
  const pages = [];
  for (let i = 0; i < cabins.length; i += CABINS_PER_PAGE) {
    pages.push(cabins.slice(i, i + CABINS_PER_PAGE));
  }
  const totalPages = pages.length;
  const visibleCabins = pages[currentPage] || [];

  useEffect(() => { setCurrentPage(0); }, [totalPages]);

  useEffect(() => {
    if (totalPages <= 1) return;
    const t = setInterval(() => {
      setCurrentPage((p) => (p + 1) % totalPages);
    }, PAGE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [totalPages]);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await displayService.getQueue();
      setCabins(res.data);
      setLastUpdated(new Date());
      setFetchError(null);
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.response?.statusText
        || err?.message
        || 'Network error';
      setFetchError(`API error (${err?.response?.status ?? 'no response'}): ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const t = setInterval(fetchQueue, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchQueue]);

  const timeStr = lastUpdated
    ? lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : '';

  const dateStr = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  // Grid column logic:
  //   1 cabin  → full width (12)
  //   2 cabins → 2 per row  (6)
  //   3+       → 3 per row  (4)  — at least 3 always fit on one screen
  const count = visibleCabins.length;
  const gridCols = count === 1 ? 12 : count === 2 ? 6 : 4;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#0a1520',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Montserrat", "Roboto", sans-serif',
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          background: 'linear-gradient(90deg, #0d47a1, #01579b)',
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 16px rgba(0,0,0,0.5)',
        }}
      >
        <Box>
          <Typography variant="h6" color="white" fontWeight={800} sx={{ letterSpacing: 1 }}>
            VEDANTA HOSPITAL
          </Typography>
          <Typography variant="caption" color="#90caf9">
            OPD Patient Queue — {dateStr}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" color="white" fontWeight={700} fontFamily="monospace">
            <LiveClock />
          </Typography>
          {timeStr && (
            <Typography variant="caption" color="#90caf9">
              Updated: {timeStr}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Page progress bar */}
      {totalPages > 1 && <PageProgressBar key={currentPage} duration={PAGE_INTERVAL_MS} />}

      {/* Content */}
      <Box sx={{ flex: 1, p: { xs: 1.5, md: 2 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: 2 }}>
            <CircularProgress sx={{ color: '#90caf9' }} size={48} />
            <Typography color="#546e7a">Loading queue…</Typography>
          </Box>
        ) : fetchError ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 2 }}>
            <Typography variant="h5" color="#ef5350" fontWeight={700} textAlign="center">
              Could Not Load Queue
            </Typography>
            <Typography color="#78909c" textAlign="center" sx={{ maxWidth: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>
              {fetchError}
            </Typography>
            <Typography color="#546e7a" textAlign="center" variant="caption">
              Retrying every 30 seconds…
            </Typography>
          </Box>
        ) : cabins.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '60vh',
              gap: 2,
            }}
          >
            <Typography variant="h4" color="#37474f" fontWeight={700} textAlign="center">
              No Active OPD Sessions
            </Typography>
            <Typography color="#546e7a" textAlign="center">
              Go to <strong style={{ color: '#78909c' }}>Admin → Cabin Assignment</strong>, assign doctors to cabins and click <strong style={{ color: '#78909c' }}>Confirm &amp; Go Live</strong>.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {visibleCabins.map((cabin) => (
              <Grid item key={cabin.cabinId} xs={12} sm={gridCols} md={gridCols}>
                <CabinCard cabin={cabin} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Bottom bar */}
      <Box
        sx={{
          background: 'rgba(0,0,0,0.5)',
          px: 3,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <PageDots total={totalPages} current={currentPage} />
        <Typography variant="caption" color="#37474f" sx={{ letterSpacing: 1 }}>
          {totalPages > 1
            ? `Page ${currentPage + 1} of ${totalPages} — rotates every 15 seconds  |  Please wait for your queue number to be called`
            : 'Screen auto-refreshes every 30 seconds  |  Please wait for your queue number to be called'}
        </Typography>
      </Box>
    </Box>
  );
}
